import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * 通用 API 載入 hook，處理 loading / error / data 狀態。
 *
 * 用法：
 *   const { data, loading, error, refetch } = useApi(() => getProducts({ featured: true }), []);
 *   const { data: list = [] } = useApi(...);   // ← 預設值正常運作
 *
 * @param fetcher  () => Promise<T>
 * @param deps     重新發動條件（與 useEffect 相同）
 *
 * 注意：data 初始值為 undefined（非 null），讓呼叫端的解構預設值
 *      `const { data: list = [] }` 能正確生效。若 fetcher 真的回傳 null
 *      （例如「找不到此商品」），呼叫端可用 if (!data) 處理，因為 null/undefined
 *      皆為 falsy。
 */
export function useApi(fetcher, deps = []) {
  const [data, setData] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      if (!cancelled.current) setData(result);
    } catch (err) {
      if (!cancelled.current) setError(err);
    } finally {
      if (!cancelled.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    cancelled.current = false;
    run();
    return () => { cancelled.current = true; };
  }, [run]);

  return { data, loading, error, refetch: run };
}
